import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { translations, loadTranslations, Language, Translations } from "./translations";

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = "elevest-lang";

/**
 * Resolve the initial language with this priority:
 *   1. ?lang=en|de in the URL  (overrides everything, persisted)
 *   2. Previously stored manual choice (localStorage)
 *   3. Default → "de"
 */
const resolveInitialLanguage = (): Language => {
  if (typeof window === "undefined") return "de";

  try {
    const params = new URLSearchParams(window.location.search);
    const urlLang = params.get("lang")?.toLowerCase();
    if (urlLang === "en" || urlLang === "de") {
      window.localStorage.setItem(STORAGE_KEY, urlLang);
      return urlLang as Language;
    }
  } catch {
    // ignore
  }

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "en" || stored === "de") return stored as Language;
  } catch {
    // ignore
  }

  return "de";
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<Language>(resolveInitialLanguage);
  // Bump when an async translation bundle finishes loading so consumers re-render
  const [, setRev] = useState(0);

  // Ensure the bundle for the active language is loaded
  useEffect(() => {
    let cancelled = false;
    loadTranslations(lang).then(() => {
      if (!cancelled) setRev((r) => r + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const setLang = (next: Language) => {
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
  };

  // Keep <html lang="…"> in sync for accessibility & SEO
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  // Strip ?lang=… from the URL after consumption to avoid duplicate URLs in
  // Google Search Console ("Alternative page with proper canonical tag").
  // Language is already persisted in localStorage; the query param is no
  // longer needed once resolveInitialLanguage() has read it.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const url = new URL(window.location.href);
      if (url.pathname.startsWith("/newsletter/") && url.searchParams.has("preview")) return;
      if (url.searchParams.has("lang")) {
        url.searchParams.delete("lang");
        const cleaned = url.pathname + (url.search ? url.search : "") + url.hash;
        window.history.replaceState(window.history.state, "", cleaned);
      }
    } catch {
      // ignore
    }
  }, []);


  const t = translations[lang];

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
};
